import React, { useEffect, useState, useRef, useCallback } from 'react'
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { isDTS } from '../../../utility/platform';
import { getEntryTypeLabel } from '../../../utility/complianceCopy';

// compliance-ok: tab ids, not labels — rendered through getEntryTypeLabel
const DEFAULT_ITEMS = ["Straight Bet", "Parlay Bet", "Teaser", "Call Bets"];

export default function BuilderSlider({
  getCurrentSelectValue,
  activeTab,
  items = DEFAULT_ITEMS,
}) {
    const scrollLevel = 120
    const sliderRef = useRef(null);
    const wasMeasurableRef = useRef(false);
    const [selectValue, setSelectedValue] = useState(activeTab || "Straight Bet"); // compliance-ok: tab id
    const [disable, setDisable] = useState({ leftBtn: true, rightBtn: true });
    const [isScrollable, setIsScrollable] = useState(false);

    // Four entry-type tabs never fit a phone sheet; keep < > in the layout so
    // the first open matches the intended strip (left disabled, right active).
    const showArrows = isScrollable || items.length > 3;

    const updateScrollState = useCallback((opts = {}) => {
        const el = sliderRef.current;
        if (!el) return;
        const { scrollWidth, clientWidth } = el;
        const measurable = clientWidth > 0;
        const scrollable = measurable && scrollWidth > clientWidth + 1;

        // Entry Builder sheet starts as display:none. The first real width means
        // the sheet just opened — pin the strip to the start (Straight Entry).
        if (opts.resetToStart || (measurable && !wasMeasurableRef.current)) {
            el.scrollTo({ left: 0, behavior: "auto" });
            wasMeasurableRef.current = measurable;
        }
        if (!measurable) {
            wasMeasurableRef.current = false;
        } else {
            wasMeasurableRef.current = true;
        }

        setIsScrollable((prev) => (prev === scrollable ? prev : scrollable));

        const left = el.scrollLeft;
        const nextDisable = scrollable
            ? {
                leftBtn: left <= 1,
                rightBtn: left + clientWidth >= scrollWidth - 1,
            }
            : { leftBtn: true, rightBtn: true };

        setDisable((prev) => (
            prev.leftBtn === nextDisable.leftBtn && prev.rightBtn === nextDisable.rightBtn
                ? prev
                : nextDisable
        ));
    }, []);

    const scrollLeft = () => {
        const el = sliderRef.current;
        if (!el) return;
        el.scrollBy({ left: -scrollLevel, behavior: "smooth" });
        window.setTimeout(() => updateScrollState(), 320);
    };

    const scrollRight = () => {
        const el = sliderRef.current;
        if (!el) return;
        el.scrollBy({ left: scrollLevel, behavior: "smooth" });
        window.setTimeout(() => updateScrollState(), 320);
    };

    const selectHandle = (item) => {
        setSelectedValue(item);
    };

    useEffect(() => {
        const el = sliderRef.current;
        if (!el) return;

        updateScrollState({ resetToStart: true });

        const ro = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(() => {
                requestAnimationFrame(() => updateScrollState());
            })
            : null;
        ro?.observe(el);
        const container = el.closest(".betBuildContainer");
        if (container) ro?.observe(container);
        if (el.parentElement) ro?.observe(el.parentElement);

        // Phone sheet becomes visible after mount — remeasure a couple of times.
        const t1 = window.setTimeout(() => updateScrollState({ resetToStart: true }), 50);
        const t2 = window.setTimeout(() => updateScrollState(), 250);

        el.addEventListener("scroll", updateScrollState, { passive: true });
        window.addEventListener("resize", updateScrollState);

        const shell = document.querySelector(".phone-shell");
        let wasEntryOpen = !!shell?.classList.contains("phone-entry-open");
        const mo = shell && typeof MutationObserver !== "undefined"
            ? new MutationObserver(() => {
                // Sheet open toggles phone-entry-open — reset strip to start once.
                const open = shell.classList.contains("phone-entry-open");
                if (open && !wasEntryOpen) {
                    updateScrollState({ resetToStart: true });
                } else {
                    updateScrollState();
                }
                wasEntryOpen = open;
            })
            : null;
        mo?.observe(shell, { attributes: true, attributeFilter: ["class"] });

        return () => {
            ro?.disconnect();
            mo?.disconnect();
            window.clearTimeout(t1);
            window.clearTimeout(t2);
            el.removeEventListener("scroll", updateScrollState);
            window.removeEventListener("resize", updateScrollState);
        };
    }, [items.length, updateScrollState]);

    useEffect(() => {
        if (activeTab && activeTab !== selectValue) {
            setSelectedValue(activeTab);
        }
    }, [activeTab]);

    useEffect(() => {
        if (selectValue) {
            getCurrentSelectValue(selectValue);
        }
    }, [selectValue]);

    return (
        <div className={`customSlider${showArrows ? " is-scrollable" : ""}`}>
            {showArrows && (
                <button
                    type="button"
                    onClick={scrollLeft}
                    className="customSliderLeftBtn"
                    disabled={disable.leftBtn}
                    aria-label="Previous entry types"
                >
                    <IoIosArrowBack />
                </button>
            )}
            <ul ref={sliderRef}>
                {items.map((item) => (
                    // `item` stays the raw tab identifier everywhere — the key, the
                    // selected-state comparison and the value handed back to the
                    // parent. Only the text the user reads is translated.
                    <li
                        key={item}
                        className={`fs14 ${selectValue === item ? "isSelected" : ""}`}
                        onClick={() => selectHandle(item)}
                    >
                        {getEntryTypeLabel(item, isDTS())}
                    </li>
                ))}
            </ul>
            {showArrows && (
                <button
                    type="button"
                    onClick={scrollRight}
                    className="customSliderRightBtn"
                    disabled={disable.rightBtn}
                    aria-label="Next entry types"
                >
                    <IoIosArrowForward />
                </button>
            )}
        </div>
    );
}
